import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import ProductGallery from './ProductGallery';
import GalleryLink from '../gallery/GalleryLink';
import ProductIcon from './ProductIcon';
import PostcodeChecker from '../PostcodeChecker';
import { CheckIcon } from '../icons';
import { getPricing, fmtPrice, formatSaleEnds } from '../../utils/pricing';
import { availableVariants } from '../../utils/stock';
import { LEAD_STOCK, MADE_TO_ORDER_BANDS } from '../../config/leadTimes';
import { DELIVERY_SUMMARY } from '../../utils/delivery';
import './ProductBuyHero.css';

// The top of an editorial product page: everything needed to buy, above the
// fold - name, price, what is in the box, six key facts, the two ways to buy,
// when it arrives and what delivery costs.
//
// Laptop: photos on the left with the quick-buy widget under them; the buy
// column on the right (name, price, facts, buttons) stays in view while the
// left side scrolls. Phone: name and price, then the photos, then the rest,
// then the widget - a phone visitor sees the product before the fine print.
//
// `children` is the quick-buy widget (the configurator), passed in by the page
// so this component does not need to know how an awning is configured. The
// "Buy standard size" button jumps to it.

function joinList(items) {
  if (items.length <= 1) return items[0] || '';
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

// What is ready to ship, read from the product's real inventory - never
// assumed from a colour name. Nothing in stock means nothing is claimed.
function stockLine(product) {
  const colours = [...new Set(availableVariants(product).flatMap((r) => r.colours))];
  if (!colours.length) return { inStock: false, text: `Made to order: ${MADE_TO_ORDER_BANDS}.` };
  return { inStock: true, text: `${joinList(colours)} in stock, ships ${LEAD_STOCK}. Everything else made to order: ${MADE_TO_ORDER_BANDS}.` };
}

export default function ProductBuyHero({ product, hero, reviews, images, leadImage, configureUrl, quoteUrl, category, children }) {
  const [postcodeOpen, setPostcodeOpen] = useState(false);
  const pricing = getPricing(product);
  const stock = stockLine(product);
  const rated = reviews && reviews.items && reviews.items.length > 0 && reviews.rating != null;

  return (
    <section className="pbh">
      <div className="pbh-inner">
        {/* The buy column. On a phone it dissolves into the grid so the
            photos can sit between the price and the rest of it. */}
        <div className="pbh-side">
          <div className="pbh-head">
            {category && <p className="pbh-eyebrow">{category}</p>}
            <h1 className="pbh-title">{product.name}</h1>
            {hero.subhead && <p className="pbh-subhead">{hero.subhead}</p>}
            {pricing && (
              <p className="pbh-price">
                {pricing.original != null && <s className="pbh-price-was">${fmtPrice(pricing.original)}</s>}
                <span className="pbh-price-from">From</span> ${fmtPrice(pricing.sale)}
                <span className="pbh-price-gst">incl. GST</span>
              </p>
            )}
            {pricing && pricing.saleActive && pricing.saleEndsAt && (
              <p className="pbh-sale">{pricing.saleLabel ? `${pricing.saleLabel} - ` : ''}{formatSaleEnds(pricing.saleEndsAt)}</p>
            )}
            {hero.included && <p className="pbh-included"><CheckIcon />{hero.included}</p>}
          </div>

          <div className="pbh-body">
            {hero.keyFacts?.length > 0 && (
              <ul className="pbh-facts">
                {hero.keyFacts.map((f) => (
                  <li key={f.text}><ProductIcon name={f.icon} /><span>{f.text}</span></li>
                ))}
              </ul>
            )}

            <div className="pbh-actions">
              <Link to={configureUrl} className="pbh-btn pbh-btn--primary">Customise and price</Link>
              <a href="#buy-standard" className="pbh-btn pbh-btn--secondary">Buy standard size</a>
            </div>
            <Link to={quoteUrl} className="pbh-textlink">Need something custom? Request a quote</Link>

            <div className="pbh-lines">
              <p className="pbh-line">
                <span className={`pbh-dot${stock.inStock ? ' pbh-dot--stock' : ''}`} aria-hidden="true" />
                {stock.text}
              </p>
              <p className="pbh-line">
                <span className="pbh-dot" aria-hidden="true" />
                <span>
                  {DELIVERY_SUMMARY}{' '}
                  <button
                    type="button"
                    className="pbh-inline-btn"
                    aria-expanded={postcodeOpen}
                    onClick={() => setPostcodeOpen((o) => !o)}
                  >Check your postcode</button>
                </span>
              </p>
              {postcodeOpen && <div className="pbh-postcode"><PostcodeChecker compact /></div>}
            </div>

            {(rated || hero.trust?.length > 0) && (
              <ul className="pbh-trust">
                {rated && (
                  <li>
                    <span className="pbh-star" aria-hidden="true">★</span> {reviews.rating.toFixed(1)} from {reviews.count} reviews
                  </li>
                )}
                {(hero.trust || []).map((t) => <li key={t}>{t}</li>)}
              </ul>
            )}
          </div>

        </div>

        <div className="pbh-gallery">
          <ProductGallery images={images} leadImage={leadImage} name={product.name} />
          <GalleryLink productSlug={product.slug} className="pbh-gallery-link" />
        </div>

        {children && <div className="pbh-buy" id="buy-standard">{children}</div>}
      </div>
    </section>
  );
}
